/**
 * Find a good spot for a part: used when a kid TAPS a tray item instead of dragging it.
 * Prefers spots where its snaps land on existing snaps (it connects!), then spots near
 * the middle of the board, then flat (layer 0), then the default direction.
 */
import { partDef } from '../parts/registry';
import { footprint, type Board } from './board';
import type { PartProps, PartType, Placement, Rot } from './types';

/**
 * How well a candidate connects: +1 for each of its snaps landing on someone else's snap.
 * Landing BOTH snaps on the two snaps of one part bypasses that part (it would never light),
 * so that scores badly; so does stacking on top of another part.
 */
export function connectScore(board: Board, p: Placement, layer: number, exclude?: string): number {
  let n = 0;
  // Which non-wire parts have a snap under each of our snaps (anywhere in the stack)?
  const under: Set<string>[] = [];
  for (const c of footprint(p)) {
    if (c.pin < 0) continue;
    const stack = board.stackAt(c.x, c.y, exclude);
    const top = stack[stack.length - 1];
    if (top && top.cell.pin >= 0) n++;
    under.push(
      new Set(
        stack
          .filter((s) => s.cell.pin >= 0 && s.part.type !== 'wire' && s.part.type !== 'jumper')
          .map((s) => s.part.id),
      ),
    );
  }
  const [a, b] = under;
  if (a && b && [...a].some((id) => b.has(id))) n -= 6;
  return n - layer * 0.05;
}

export function findSpot(board: Board, type: PartType, props: PartProps): Placement | null {
  const full = { ...partDef(type).defaultProps(), ...props };
  const cx = (board.cols - 1) / 2;
  const cy = (board.rows - 1) / 2;
  let best: { p: Placement; score: number } | null = null;
  for (let y = 0; y < board.rows; y++)
    for (let x = 0; x < board.cols; x++)
      for (const rot of [0, 1, 2, 3] as Rot[]) {
        const p: Placement = { id: '__spot', type, x, y, rot, layer: 0, props: full };
        const cells = footprint(p);
        if (!cells.every((c) => board.inBounds(c))) continue;
        const chk = board.check(p);
        if (!chk.ok) continue;
        const connects = connectScore(board, p, chk.layer);
        const mx = cells.reduce((s, c) => s + c.x, 0) / cells.length;
        const my = cells.reduce((s, c) => s + c.y, 0) / cells.length;
        const score =
          connects * 10 -
          Math.hypot(mx - cx, my - cy) -
          chk.layer * 2 -
          (rot % 2 ? 0.3 : 0) -
          (rot > 1 ? 0.2 : 0);
        if (!best || score > best.score) best = { p: { ...p, layer: chk.layer }, score };
      }
  return best?.p ?? null;
}
